import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { PhotoResponse } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { getMirroredPlacements } from "../search/catalogStore";
import { useT } from "../../shared/i18n";
import { FilterTable, type FilterRow } from "../../shared/components/FilterTable";
import { PhotoGrid } from "./PhotoGrid";
import {
  DEFAULT_GALLERY_FILTERS,
  readGalleryFilters,
  writeGalleryFilters,
  type PhotoFilter,
  type PhotoSource,
} from "./galleryFilterUrl";

/**
 * The default gallery mode: every photo, fetched from the server and filtered by
 * source (container vs item photos) and item-photo type. The filters are the
 * shared `FilterTable` (filter-table-reuse 01/08). Type applies to item photos
 * only, so container photos stay visible under Source = All; choosing Container
 * photos hides and clears Type.
 */
export function GalleryBrowse() {
  const t = useT();
  const [photos, setPhotos] = useState<PhotoResponse[]>([]);
  const [failed, setFailed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Serialized-key memo keeps the arrays referentially stable while the URL is
  // unchanged (the grid's filter memo should only rerun on real changes).
  const paramsKey = searchParams.toString();
  const urlFilters = useMemo(
    () => readGalleryFilters(new URLSearchParams(paramsKey)),
    [paramsKey],
  );
  const { filter, source, owners } = urlFilters;
  const pool = useAppStore(readableContainers);

  const updateFilters = (patch: Partial<typeof urlFilters>) => {
    setSearchParams(
      (prev) => writeGalleryFilters(prev, { ...readGalleryFilters(prev), ...patch }),
      { replace: true },
    );
  };

  useEffect(() => {
    let stale = false;
    setFailed(false);
    // The gallery is not a search result: it always lists every photo
    // (shell-redesign 07, spec §4). The search query lives in /search.
    void requests.search.photos()
      .then((res) => {
        if (!stale) setPhotos(res.photos);
      })
      .catch(() => {
        if (!stale) setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, []);

  // Owner for a photo: container photos use their container's owner; item
  // photos use the owner of the placement container holding the item; an
  // unplaced item is unassigned.
  const ownerOf = useMemo(() => {
    const byId = new Map(pool.map((container) => [container.id, container]));
    const hostByItem = new Map<string, string>();
    for (const placement of getMirroredPlacements()) {
      const host = byId.get(placement.containerId);
      if (host && !hostByItem.has(placement.itemId)) {
        hostByItem.set(placement.itemId, host.owner ?? "");
      }
    }
    return (photo: PhotoResponse): string => {
      if (photo.containerId) return byId.get(photo.containerId)?.owner ?? "";
      if (photo.itemId) return hostByItem.get(photo.itemId) ?? "";
      return "";
    };
  }, [pool]);

  const counts = useMemo(() => {
    const containerPhotos = photos.filter((photo) => photo.containerId).length;
    const itemPhotos = photos.filter((photo) => photo.itemId);
    return {
      containers: containerPhotos,
      items: itemPhotos.length,
      general: itemPhotos.filter((photo) => photo.itemPhotoType === "general").length,
      nutrition: itemPhotos.filter((photo) => photo.itemPhotoType === "nutrition").length,
    };
  }, [photos]);

  // Source + Type are the context for both the grid and the owner counts.
  const sourceTypePhotos = useMemo(() => {
    const bySource =
      source === "containers"
        ? photos.filter((photo) => photo.containerId)
        : source === "items"
          ? photos.filter((photo) => photo.itemId)
          : photos;
    return filter === "all" || source === "containers"
      ? bySource
      : bySource.filter((photo) => !photo.itemId || photo.itemPhotoType === filter);
  }, [photos, source, filter]);

  const filtered = useMemo(() => {
    if (owners.length === 0) return sourceTypePhotos;
    return sourceTypePhotos.filter((photo) => owners.includes(ownerOf(photo)));
  }, [sourceTypePhotos, owners, ownerOf]);

  // Owner options are contextual to Source + Type (not to their own selection).
  const ownerCounts = useMemo(() => {
    const countsByOwner = new Map<string, number>();
    for (const photo of sourceTypePhotos) {
      const owner = ownerOf(photo);
      countsByOwner.set(owner, (countsByOwner.get(owner) ?? 0) + 1);
    }
    for (const owner of owners) {
      if (!countsByOwner.has(owner)) countsByOwner.set(owner, 0);
    }
    return countsByOwner;
  }, [sourceTypePhotos, ownerOf, owners]);

  if (failed) {
    return (
      <Typography variant="body1" color="error">
        {t("loadError")}
      </Typography>
    );
  }

  if (photos.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("galleryEmpty")}
      </Typography>
    );
  }

  const sourceLabel =
    source === "containers"
      ? t("gallerySourceContainers")
      : source === "items"
        ? t("gallerySourceItems")
        : "";
  const typeLabel =
    filter === "general"
      ? t("galleryFilterGeneral")
      : filter === "nutrition"
        ? t("galleryFilterNutrition")
        : "";

  const rows: FilterRow[] = [
    {
      key: "photo-source",
      label: t("gallerySource"),
      values: [
        {
          value: "all",
          label: t("galleryFilterAll"),
          count: photos.length,
          selected: source === "all",
          testId: "photo-source-all",
        },
        ...(counts.containers > 0 || source === "containers"
          ? [
              {
                value: "containers",
                label: t("gallerySourceContainers"),
                count: counts.containers,
                selected: source === "containers",
                testId: "photo-source-containers",
              },
            ]
          : []),
        ...(counts.items > 0 || source === "items"
          ? [
              {
                value: "items",
                label: t("gallerySourceItems"),
                count: counts.items,
                selected: source === "items",
                testId: "photo-source-items",
              },
            ]
          : []),
      ],
      onSelect: (value: string) => {
        const next = value as PhotoSource;
        // Container photos have no item type; the Type row disappears with the
        // selection, so clear it rather than leaving an invisible filter.
        updateFilters({ source: next, filter: next === "containers" ? "all" : filter });
      },
    },
    {
      key: "photo-owner",
      label: t("filterOwner"),
      values: [...ownerCounts.entries()]
        .sort(([left], [right]) => {
          if (left === right) return 0;
          if (left === "") return 1;
          if (right === "") return -1;
          return left.localeCompare(right);
        })
        .map(([owner, count]) => ({
          value: owner,
          label: owner || t("filterUnassigned"),
          count,
          selected: owners.includes(owner),
          testId: `photo-owner-${owner || "unassigned"}`,
        })),
      onSelect: (value: string) =>
        updateFilters({
          owners: owners.includes(value)
            ? owners.filter((entry) => entry !== value)
            : [...owners, value],
        }),
    },
    ...(source === "containers"
      ? []
      : [
          {
            key: "photo-type",
            label: t("filterType"),
            values: [
              {
                value: "all",
                label: t("galleryFilterAll"),
                count: counts.items,
                selected: filter === "all",
                testId: "photo-type-all",
              },
              ...(counts.general > 0 || filter === "general"
                ? [
                    {
                      value: "general",
                      label: t("galleryFilterGeneral"),
                      count: counts.general,
                      selected: filter === "general",
                      testId: "photo-type-general",
                    },
                  ]
                : []),
              ...(counts.nutrition > 0 || filter === "nutrition"
                ? [
                    {
                      value: "nutrition",
                      label: t("galleryFilterNutrition"),
                      count: counts.nutrition,
                      selected: filter === "nutrition",
                      testId: "photo-type-nutrition",
                    },
                  ]
                : []),
            ],
            onSelect: (value: string) => updateFilters({ filter: value as PhotoFilter }),
          },
        ]),
  ];

  const activeCount =
    (source === "all" ? 0 : 1) +
    (filter === "all" || source === "containers" ? 0 : 1) +
    owners.length;
  const ownerSummary = owners.map((owner) => owner || t("filterUnassigned")).join(" / ");
  const summary = [
    sourceLabel === "" ? "" : `${t("gallerySource")}: ${sourceLabel}`,
    ownerSummary === "" ? "" : `${t("filterOwner")}: ${ownerSummary}`,
    typeLabel === "" ? "" : `${t("filterType")}: ${typeLabel}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box>
      <FilterTable
        rows={rows}
        activeCount={activeCount}
        summary={summary}
        onClear={() => updateFilters(DEFAULT_GALLERY_FILTERS)}
      />
      <PhotoGrid photos={filtered} />
    </Box>
  );
}
